<script setup>
import { ref } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useAuthStore } from '@/stores/auth.js'
import { useToast } from 'primevue/usetoast'
import { Form } from '@primevue/forms';
import { FormField } from '@primevue/forms';
import InputText from 'primevue/inputtext'
import Password from 'primevue/password'
import Message from 'primevue/message'
import Button from 'primevue/button'
import catSvg from '@/assets/cat.svg'


const mode = ref('login') // 'login' | 'forgot'
const loading = ref(false)
const auth = useAuthStore()
const router = useRouter()
const route = useRoute()
const toast = useToast()

// Примитивная валидация (можно подключить zod/yup через resolver)
function required(v) { return (!v || String(v).trim() === '') ? 'Обязательное поле' : undefined }

// Локальные модели для простоты
const loginUsername = ref('')
const loginEmail = ref('')
const loginPassword = ref('')
const resetEmail = ref('')

async function submitLogin() {
  const userErr = required(loginUsername.value)
  const passErr = required(loginPassword.value)
  if (userErr || passErr) {
    toast.add({ severity: 'warn', summary: 'Проверьте поля', life: 2000 })
    return
  }
  loading.value = true
  try {
    await auth.login({ username: loginUsername.value, password: loginPassword.value })
    const target = route.query.redirect || '/'
    await router.replace(String(target))
  } catch (e) {
    const msg = e?.response?.data?.detail || 'Неверные учётные данные'
    toast.add({ severity: 'error', summary: 'Вход не выполнен', detail: msg, life: 4000 })
  } finally {
    loading.value = false
  }
}

async function submitForgot() {
  const emailErr = required(resetEmail.value)
  if (emailErr) {
    toast.add({ severity: 'warn', summary: 'Укажите e‑mail', life: 2000 })
    return
  }
  loading.value = true
  try {
    await auth.requestPasswordReset(resetEmail.value)
    toast.add({ severity: 'success', summary: 'Письмо отправлено', life: 2500 })
    mode.value = 'login'
  } catch (e) {
    const msg = e?.response?.data?.detail || 'Не удалось отправить письмо'
    toast.add({ severity: 'error', summary: 'Ошибка', detail: msg, life: 4000 })
  } finally {
    loading.value = false
  }
}
</script>

<template>
  <div class="min-h-screen bg-white flex items-center justify-center p-4 relative">
    <div class="w-full max-w-sm">
      <h1 class="text-center text-xl font-semibold mb-6">
        {{ mode === 'login' ? 'Авторизация' : 'Восстановление доступа' }}
      </h1>

      <div v-if="mode === 'login'">
        <Form class="flex flex-col gap-4 w-full">
        <FormField v-slot="$field" as="section" name="username" :validate="() => required(loginUsername)" class="flex flex-col gap-2">
          <InputText v-model="loginUsername" type="text" placeholder="Логин" autocomplete="username" />
          <Message v-if="$field?.invalid" severity="error" size="small" variant="simple">{{ $field.error?.message }}</Message>
          </FormField>

          <FormField v-slot="$field" asChild name="password">
            <section class="flex flex-col gap-2">
              <Password v-model="loginPassword" type="password" placeholder="Пароль" :feedback="false" toggleMask fluid />
              <Message v-if="$field?.invalid" severity="error" size="small" variant="simple">{{ $field.error?.message }}</Message>
            </section>
          </FormField>

          <div class="flex items-center justify-between">
            <Button type="button" link label="Забыл пароль" @click="mode = 'forgot'" />
            <Button type="button" :loading="loading" severity="secondary" label="Войти" @click="submitLogin" />
          </div>
        </Form>
      </div>

      <div v-else>
        <Form class="flex flex-col gap-4 w-full">
          <FormField v-slot="$field" as="section" name="reset_email" :validate="() => required(resetEmail)" class="flex flex-col gap-2">
            <InputText v-model="resetEmail" type="email" placeholder="E‑mail" />
            <Message v-if="$field?.invalid" severity="error" size="small" variant="simple">{{ $field.error?.message }}</Message>
          </FormField>

          <div class="flex items-center justify-between">
            <Button type="button" link label="Назад к входу" @click="mode = 'login'" />
            <Button type="button" :loading="loading" severity="secondary" label="Отправить" @click="submitForgot" />
          </div>
        </Form>
      </div>
    </div>
    <div class="fixed bottom-0 left-0 w-full flex flex-col items-center pb-3 pointer-events-none">
      <img :src="catSvg" alt="cat" class="h-40 opacity-90 select-none" />
      <div class="mt-1 text-sm text-slate-500 select-none">powered by wrclab.com</div>
    </div>

  </div>
</template>
